import { updateDailyChecklist } from "./function";
import { DailyPlanData, FormFieldConfig, Journal, TradeExecution, TradingSetup, UpdateDailyJournalPayload } from "./type";
type DailyPlanKey = keyof DailyPlanData;
type FormFieldKey = "id" | "phase" | "step" | "section_title" | "field_key" | "field_label" | "input_type" | "options" | "is_required" | "order_index" | "weight_percent" | "created_at" | "updated_at";

function getFormConfigsField(search_key: FormFieldKey, search_value: string, form_configs: FormFieldConfig[]): FormFieldConfig | undefined {
  return form_configs.find(f => f[search_key] === search_value);
}

function getAllFormConfigsField(search_key: FormFieldKey, search_value: string, form_configs: FormFieldConfig[]): FormFieldConfig[] {
  return form_configs.filter(f => f[search_key] === search_value);
}

function convertRatingConfigsFieldToValue(optionField: FormFieldConfig['options']): Record<string, number> {
  const result: Record<string, number> = {};
  
  optionField?.forEach((option: any, index: number) => {
    // Nếu option là string thì dùng trực tiếp, nếu là object thì lấy value hoặc label_vi/label_en
    const key = typeof option === 'string' 
      ? option 
      : (option?.value || option?.label_vi || option?.label_en || option?.label);

    if (key) {
      result[String(key)] = index;
    }
  });

  return result;
}

// Chuyển đổi Hướng giao dịch
const mapTextToDir = (text: string): 'buy' | 'sell' | 'NEUTRAL' | null => {
  if (text === 'BUY' || text?.toLowerCase() === 'buy') return 'buy';
  if (text === 'SELL' || text?.toLowerCase() === 'sell') return 'sell';
  if (text?.includes('ngoại') || text?.toLowerCase() === 'neutral') return 'NEUTRAL';
  return null;
};

// Chuyển đổi Thanh khoản
const mapTextToLiqType = (text: string): 'rev' | 'cont' | 'unclear' | null => {
  if (text?.includes('Quét')) return 'rev';
  if (text?.includes('Phá')) return 'cont';
  if (text?.includes('rõ') || text?.includes('Chưa')) return 'unclear';
  return null;
};

// Chuyển đổi Nhiễu/Sạch
const mapTextToClean = (text: string): 'clean' | 'noise' | null => {
  if (text?.includes('buy')) return 'clean';
  if (text?.includes('sell')) return 'noise';
  return null;
};

// Chuyển đổi Technical Context (API -> UI)
const mapTCApiToUi = (text: string | undefined, options: string[]): string => {
  if (!text) return '';
  const cleanText = text.trim();
  const textUpper = cleanText.toUpperCase();

  // 1. Khớp chính xác hoàn toàn (Nếu API trả về đúng chuỗi UI)
  const exactMatch = options.find(opt => opt.toLowerCase() === cleanText.toLowerCase());
  if (exactMatch) return exactMatch;

  // 2. Xử lý khi API trả về Enum Tiếng Anh ("BULLISH", "BEARISH", "SIDEWAYS", "CONFLICT")
  if (textUpper.includes('BULLISH')) {
    // Tìm option chứa chữ "Tăng" hoặc "Bullish"
    return options.find(opt => opt.includes('Tăng') || opt.toUpperCase().includes('BULLISH')) || options[0] || '';
  }
  
  if (textUpper.includes('BEARISH')) {
    // Tìm option chứa chữ "Giảm" hoặc "Bearish"
    return options.find(opt => opt.includes('Giảm') || opt.toUpperCase().includes('BEARISH')) || options[1] || '';
  }
  
  if (textUpper.includes('SIDEWAY') || textUpper.includes('CONFLICT')) {
    // Tìm option chứa chữ "ngang", "Xung đột" hoặc "Sideway"
    return options.find(opt => opt.includes('ngang') || opt.includes('Xung đột') || opt.toUpperCase().includes('SIDEWAY')) || options[2] || '';
  }

  // 3. Fallback: Kiểm tra xem chuỗi UI có chứa từ khóa API hay ngược lại không
  const matched = options.find(opt => 
    opt.toLowerCase().includes(cleanText.toLowerCase()) || 
    cleanText.toLowerCase().includes(opt.toLowerCase())
  );

  return matched || '';
};

// Chuyển đổi Chuỗi phiên (Ví dụ: "Phiên Á, Phiên Âu" -> ["asia", "london"])
const mapTextToSessions = (text: string, options: { id: string; label: string }[]): string[] => {
  if (!text) return [];
  return options
    .filter(opt => text.toLowerCase().includes(opt.label.toLowerCase()) || text.toLowerCase().includes(opt.id))
    .map(opt => opt.id);
};


interface UploadFieldOptions {
  currentDate: string;
}

/**
 * Hàm cập nhật một trường dữ liệu đơn lẻ trong daily_plan_data lên Server
 * @param updateKey - Tên thuộc tính trong daily_plan_data (ví dụ: 'sleep_quality', 'max_daily_loss')
 * @param updateValue - Giá trị mới của thuộc tính đó
 * @param options - Object chứa thông tin phụ trợ như ngày hiện tại
 */
async function uploadFieldData_daily_plan_data<K extends DailyPlanKey>(
  updateKey: K,
  updateValue: DailyPlanData[K], // Giá trị phải khớp với kiểu dữ liệu của Key đó
  options: UploadFieldOptions
) {
  // 1. Tạo cấu trúc payload đúng chuẩn của API PUT yêu cầu
  const payload: UpdateDailyJournalPayload = {
    date: options.currentDate,
    daily_plan_data: {
      [updateKey]: updateValue
    }
  };

  // 2. Gọi hàm API async xử lý gửi đi
  try {
    console.log(`[API PUT] Đang cập nhật trường ${updateKey}...`, payload);
    const result = await updateDailyChecklist(payload);
    return result;
  } catch (error) {
    console.error(`Lỗi khi cập nhật trường ${updateKey}:`, error);
    throw error;
  }
}

// Loại bỏ price_base và price_margin, thêm entry_zone vào key hợp lệ
export type SetupKey = Exclude<keyof TradingSetup, 'setup_type' | 'id' | 'price_base' | 'price_margin'> | 'entry_zone';

export interface UploadFieldSetupOptions {
  date: string;
  setupType: 'BUY' | 'SELL' | 'SCENARIO';
  id?: number;
  scenarioIndex?: number;
}

async function uploadFieldData_Setup<K extends SetupKey>(
  updateKey: K,
  updateValue: any,
  options: UploadFieldSetupOptions
) {
  const { date, setupType, id, scenarioIndex } = options;

  // 1. Tạo đối tượng setup phẳng
  const targetSetup: any = {
    setup_type: setupType,
  };

  // CHỈ GÁN ID NẾU ĐƯỢC TRUYỀN VÀO (Có giá trị hợp lệ từ DB hoặc State)
  // Nếu id là undefined hoặc null, TUYỆT ĐỐI KHÔNG tự sinh maxId + 1 ở đây!
  if (id !== undefined && id !== null) {
    targetSetup.id = id;
  }

  // 2. Gán tên kịch bản nếu là loại SCENARIO
  if (setupType === 'SCENARIO') {
    const sIndex = scenarioIndex ?? 0;
    targetSetup.scenario_name = `KB${sIndex + 1} ${sIndex === 0 ? 'Kịch bản chính' : `Kịch bản ${sIndex + 1}`}`;
  }

  // Gán trường dữ liệu cần update (ví dụ: entry_zone, trigger_condition...)
  targetSetup[updateKey] = updateValue;

  // 3. Đóng gói Payload
  const payload: UpdateDailyJournalPayload = {
    date: date,
    setups: [targetSetup] // Mảng này chỉ chứa duy nhất 1 bản ghi phẳng cần thay đổi
  };

  // 4. Bắn API lên Backend
  return await updateDailyChecklist(payload);
}

function getReTradeDataFromJournal(journal: Journal | TradeExecution): {
  pre_trade_data: any;
  items: { id: number; is_checked: boolean }[];
} | null {
  const itemList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

  let trade_execution: TradeExecution | undefined;

  // Type Guard: Kiểm tra xem tham số truyền vào là Journal hay là TradeExecution
  if ('trade_executions' in journal) {
    // Nếu là Journal: Kiểm tra mảng trade_executions
    if (!journal.trade_executions || journal.trade_executions.length === 0) {
      return null;
    }
    trade_execution = journal.trade_executions[0];
  } else {
    // Nếu truyền vào trực tiếp TradeExecution
    trade_execution = journal;
  }

  // Trường hợp phòng thủ nếu trade_execution không hợp lệ
  if (!trade_execution) return null;

  // Trích xuất dữ liệu reTradeData
  const reTradeData = {
    pre_trade_data: {
      pre_main_bias: trade_execution.pre_trade_data?.pre_main_bias || null,
      bias_reason: trade_execution.pre_trade_data?.bias_reason || null,
      pre_liquidity_zone: trade_execution.pre_trade_data?.pre_liquidity_zone || null,
      scenario_guess: trade_execution.pre_trade_data?.scenario_guess || null,
      pre_market_condition: trade_execution.pre_trade_data?.pre_market_condition || null,
      pre_trading_session: trade_execution.pre_trade_data?.pre_trading_session || null,
      key_levels: trade_execution.pre_trade_data?.key_levels || null,

      prior_trade_state: trade_execution.pre_trade_data?.prior_trade_state || null,
      sl_loss_percent: trade_execution.pre_trade_data?.sl_loss_percent || null,
      dd_left_percent: trade_execution.pre_trade_data?.dd_left_percent || null,
      risk_acceptance: trade_execution.pre_trade_data?.risk_acceptance || null,
      pre_emotions: trade_execution.pre_trade_data?.pre_emotions || null,
    },

    // Dùng Optional Chaining ?. đề phòng checklist_records bị undefined
    items: (trade_execution.checklist_records || [])
      .filter(item => itemList.includes(item.item_id))
      .map(item => ({
        id: item.item_id,
        is_checked: item.is_checked,
      })),
  };

  return reTradeData;
}

// Chuyển API Enum -> UI Option


// Chuyển UI Option -> API Enum
export const mapTCUiToApi = (uiVal?: string): string | undefined => {
  if (!uiVal) return undefined;
  if (uiVal.includes('Tăng') || uiVal.includes('Đồng thuận Tăng')) return 'BULLISH';
  if (uiVal.includes('Giảm') || uiVal.includes('Đồng thuận Giảm')) return 'BEARISH';
  if (uiVal.includes('Sideway') || uiVal.includes('Đi ngang')) return 'SIDEWAYS';
  if (uiVal.includes('Xung đột')) return 'CONFLICT';
  return uiVal;
};

export interface OptionEmotion {
    category: "positive" | "negative" | "dangerous" | string;
    value: string;
    label_vi: string;
    label_en: string;
    type: "success" | "warning" | "danger" | string;
}

export interface EmotionGroup {
    categoryKey: string;
    title: string;
    color: string;
    items: OptionEmotion[];
}

/**
 * Map cấu hình màu sắc & tiêu đề hiển thị dựa trên type/category
 */
export const CATEGORY_MAP: Record<string, { title: string; color: string }> = {
    positive: { title: "Cảm xúc tích cực", color: "var(--teal, #10b981)" },
    negative: { title: "Cảm xúc tiêu cực", color: "var(--amber, #f59e0b)" },
    dangerous: { title: "Trạng thái nguy hiểm", color: "var(--red, #ef4444)" },
};

/**
 * Lấy danh sách cảm xúc được phân nhóm từ form_configs.in_trade
 */
export const getEmotionsFromFormConfig = (inTradeConfigs?: FormFieldConfig[]): EmotionGroup[] => {
    if (!inTradeConfigs || !Array.isArray(inTradeConfigs)) return [];

    // 1. Tìm field 'in_trade_emotions'
    const emotionField = inTradeConfigs.find((field) => field.field_key === "in_trade_emotions" || field.field_key === "post_trade_emotions");

    if (!emotionField || !Array.isArray(emotionField.options)) return [];

    const options = emotionField.options as unknown as OptionEmotion[];
    const groupedMap = new Map<string, EmotionGroup>();

    // 2. Phân loại theo category
    options.forEach((opt) => {
        const catKey = opt.category || "other";
        const meta = CATEGORY_MAP[catKey] || { title: catKey, color: "var(--neutral, #6b7280)" };

        if (!groupedMap.has(catKey)) {
            groupedMap.set(catKey, {
                categoryKey: catKey,
                title: meta.title,
                color: meta.color,
                items: [],
            });
        }

        groupedMap.get(catKey)!.items.push(opt);
    });

    return Array.from(groupedMap.values());
};

export {
  getFormConfigsField,
  getAllFormConfigsField,
  convertRatingConfigsFieldToValue,
  uploadFieldData_daily_plan_data,
  uploadFieldData_Setup,
  mapTextToDir,
  mapTextToLiqType,
  mapTextToClean,
  mapTextToSessions,
  mapTCApiToUi,
  getReTradeDataFromJournal
};